import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Robot,
  PaperPlaneTilt,
  BookOpen,
  Warning,
  ArrowRight,
  Sparkle,
  User,
} from '@phosphor-icons/react';

export const AskStewiePage = () => {
  const { api, isAdmin, isAuditor } = useAuth();
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [policyBooks, setPolicyBooks] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchPolicyBooks();
  }, []);

  const fetchPolicyBooks = async () => {
    try {
      const response = await api.get('/askstewie/policy-books');
      setPolicyBooks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch policy books:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading || isAuditor()) return;

    const userMessage = question.trim();
    setQuestion('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/askstewie/ask', {
        question: userMessage,
        context: {},
      });

      setMessages(prev => [...prev, {
        type: 'assistant',
        content: response.data.answer,
        citations: response.data.citations || [],
        safetyNote: response.data.safetyNote,
        nextBestAction: response.data.nextBestAction,
      }]);
    } catch (error) {
      toast.error('Failed to get response from Stewie');
      setMessages(prev => [...prev, {
        type: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedQuestions = [
    "What is the lockout/tagout procedure?",
    "How do I report a safety incident?",
    "What are the overtime policies?",
    "How do I request time off?",
  ];

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="askstewie-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-2xl mb-4">
            <Robot size={32} weight="fill" className="text-violet-600" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            AskStewie
          </h1>
          <p className="text-slate-600">
            Your AI-powered assistant for policy questions and guidance
          </p>
        </div>

        {/* Policy Books Status */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <BookOpen size={18} />
            <span>
              {policyBooks.filter(b => b.active).length} active policy book(s)
            </span>
            {isAdmin() && (
              <Badge variant="outline" className="ml-2">
                Admin: You can upload new policy books
              </Badge>
            )}
          </div>
        </div>

        {/* Chat Card */}
        <Card className="border-slate-200 shadow-soft">
          <CardContent className="p-0">
            {/* Messages Area */}
            <ScrollArea className="h-[400px] p-6">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkle size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-6">
                    Ask me anything about company policies, procedures, or best practices
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {suggestedQuestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => !isAuditor() && setQuestion(q)}
                        disabled={isAuditor()}
                        className="text-sm px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 transition-colors disabled:opacity-50"
                        data-testid={`suggested-q-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : ''}`}
                    >
                      {message.type !== 'user' && (
                        <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Robot size={18} className="text-violet-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] ${
                          message.type === 'user'
                            ? 'bg-primary text-white rounded-2xl rounded-br-md px-4 py-3'
                            : message.type === 'error'
                            ? 'bg-red-50 text-red-700 rounded-2xl rounded-bl-md px-4 py-3'
                            : 'bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        
                        {message.citations?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-500 mb-2">Sources:</p>
                            <div className="flex flex-wrap gap-2">
                              {message.citations.map((cite, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {cite.bookTitle} - {cite.section}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {message.safetyNote && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                            <Warning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800">{message.safetyNote}</p>
                          </div>
                        )}

                        {message.nextBestAction && (
                          <div className="mt-3 flex items-center gap-2 text-sm text-primary">
                            <ArrowRight size={16} />
                            <span>{message.nextBestAction}</span>
                          </div>
                        )}
                      </div>
                      {message.type === 'user' && (
                        <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <User size={18} className="text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                        <Robot size={18} className="text-violet-600" />
                      </div>
                      <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="border-t border-slate-200 p-4">
              {isAuditor() ? (
                <p className="text-center text-slate-500 text-sm py-2">
                  Auditors have view-only access to AskStewie
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="flex gap-3">
                  <Input
                    ref={inputRef}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask a question about policies or procedures..."
                    className="flex-1"
                    disabled={loading}
                    data-testid="askstewie-input"
                  />
                  <Button
                    type="submit"
                    disabled={!question.trim() || loading}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="askstewie-submit-btn"
                  >
                    <PaperPlaneTilt size={20} weight="fill" />
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AskStewiePage;
